import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SEO } from '../../seo/useSEO';
import { api, extractError } from '../../lib/api';
import { useToast } from '../../components/admin/Toast';

interface TemplateVariable {
  name: string;
  description: string;
}

export interface TemplateSummary {
  key: string;
  label: string;
  description: string;
  blockKeys: string[];
  variables: TemplateVariable[];
  defaults: { subject: string; blocks: Record<string, string> };
  override: { subject: string; blocks: Record<string, string>; updatedAt: string } | null;
}

const thClass =
  'p-3 text-left text-xs font-semibold uppercase tracking-wider text-admin-muted';

export default function AdminEmailTemplatesPage(): JSX.Element {
  const toast = useToast();
  const [items, setItems] = useState<TemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      try {
        const res = await api.get<TemplateSummary[]>('/admin/email-templates');
        setItems(res.data);
      } catch (err) {
        toast.error(extractError(err, 'Falha ao carregar templates'));
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <SEO title="Emails — Templates" noindex />
      <h1>Templates de email</h1>
      <p className="mt-2 mb-4 text-admin-text-soft">
        Personalize o texto dos emails transacionais. A estrutura e o layout mantêm-se;
        apenas o assunto e os blocos de texto são editáveis. Use{' '}
        <code className="rounded bg-admin-surface-alt px-1">{'{{variavel}}'}</code> para
        inserir valores dinâmicos.
      </p>
      <p className="mb-6 flex flex-wrap gap-x-4 gap-y-1">
        <Link
          to="/admin/emails/configuracoes"
          className="text-sm text-admin-primary hover:underline"
        >
          Configurações (logo, avaliações) →
        </Link>
        <Link
          to="/admin/emails/historico"
          className="text-sm text-admin-primary hover:underline"
        >
          Ver histórico de envios →
        </Link>
      </p>

      {loading ? (
        <p className="text-admin-text-soft">A carregar…</p>
      ) : (
        <table className="w-full overflow-hidden rounded-lg bg-admin-surface shadow-sm">
          <thead>
            <tr className="border-b border-admin-border">
              <th className={thClass}>Template</th>
              <th className={thClass}>Descrição</th>
              <th className={thClass}>Estado</th>
            </tr>
          </thead>
          <tbody>
            {items.map((t) => (
              <tr key={t.key} className="border-b border-admin-border last:border-b-0">
                <td className="p-3">
                  <Link
                    to={`/admin/emails/${t.key}`}
                    className="text-admin-primary hover:underline"
                  >
                    {t.label}
                  </Link>
                </td>
                <td className="p-3 text-admin-text-soft">{t.description}</td>
                <td className="p-3">
                  {t.override ? (
                    <span className="rounded-full bg-admin-cream px-2 py-0.5 text-xs text-admin-primary">
                      Personalizado
                    </span>
                  ) : (
                    <span className="text-xs text-admin-muted">Padrão</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
}
